//******************************************************************************
// TransformFeedback class for luma.gl v9
/*
	//--------------------------------------------------------------------------
	//vs：頂点シェーダ（文字列）
	const vs	= "...";
	//--------------------------------------------------------------------------
	//varyings：出力変数（配列またはオブジェクト）
	[{ name: "変数名", length: 要素数 }]
	//nameの代わりにkey可、lengthの代わりにsize、count可
	{ 変数名: 要素数 }
	//--------------------------------------------------------------------------
	//attributes：入力ソース（配列またはオブジェクト）
	[{ layout, data }]
	{ 変数名: { layout, data }}
	{ 変数名: 型付配列 }
	//配列の場合layoutにnameを含める必要あり
	//オブジェクトの場合layoutにnameが含まれなければキーが使用される
	//dataの代わりにvalues可
	//layoutでformatを指定すれば頂点データを整数で送信可（メモリ節約）
	//--------------------------------------------------------------------------
	//使い方
	new LumaTransform().prepare( vs, varyings ).assign( attributes, count ).run().then(
		( result ) => {
		}
	);
*/

//******************************************************************************
// import
import { Model } from "@luma.gl/engine";
import { WebGLDevice } from '@luma.gl/webgl';

//******************************************************************************
// フラグメントシェーダ
const fs = `#version 300 es
precision mediump float;
void main () {
}
`;

//******************************************************************************
// class
export class LumaTransform {
	//**************************************************************************
	// メンバ変数
	device;
	model;
	attributes;
	buffers;
	varyings;

	//**************************************************************************
	/**
	 * コンストラクタ
	 */
	constructor() {
		//----------------------------------------------------------------------
		//デバイスを作る
		const gl = document.createElement("canvas").getContext("webgl2");
		this.device = WebGLDevice.attach(gl);
	}

	//**************************************************************************
	/**
	 * モデルを作る
	 * @param {string} vs
	 * @param {Array<Object>|Object} varyings
	 * @returns {this}
	 * @throws {Error}
	*/
	prepare(vs, varyings) {
		//----------------------------------------------------------------------
		// メンバ変数を初期化
		this.varyings = {};
		//----------------------------------------------------------------------
		// 引数の型で分岐
		if (Array.isArray(varyings) === true) {
			// 配列の場合
			varyings.forEach((value) => {
				// 各要素をオブジェクトとみなし、変数名と要素数を決める
				const name = value.name ?? value.key;
				const length = value.length ?? value.size ?? value.count;
				// 決まらなければ例外
				if (typeof name === "undefined" || typeof length === "undefined") {
					throw new Error("Invalid varyings specified.");
				}
				// メンバ変数に格納
				this.varyings[name] = { length };
			});
		} else {
			// オブジェクトの場合
			Object.keys(varyings).forEach((name) => {
				// 要素数を決める
				const length = Number.isInteger(varyings[name])
					? varyings[name]
					: varyings[name].length ?? varyings[name].size ?? varyings[name].count;
				// 決まらなければ例外
				if (typeof length === "undefined") {
					throw new Error("Invalid varyings specified.");
				}
				// メンバ変数に格納
				this.varyings[name] = { length };
			});
		}
		//----------------------------------------------------------------------
		// モデルを作る
		this.model = new Model(this.device, {
			vs,
			fs,
			topology: "point-list",
			varyings: Object.keys(this.varyings)
		});
		//----------------------------------------------------------------------
		// 参照を返す
		return this;
	}

	//**************************************************************************
	/**
	 * 頂点データと頂点数の割当
	 * @param {Array|Object} sources
	 * @param {number} count
	 * @returns {this}
	 * @throws {Error}
	*/
	assign(sources, count) {
		//----------------------------------------------------------------------
		// モデルに対する割当
		// 割当で使用する変数の用意
		const bufferLayout = [];
		const attributes = this.attributes = {};
		// 引数の型で分岐
		if (Array.isArray(sources) === true) {
			// 配列の場合（各要素はオブジェクトでlayoutとvaluesの属性を持つ）
			sources.forEach((source) => {
				// バッファ用データを決める
				const data = source.data ?? source.values;
				// バリデーション
				if (
					typeof data === "undefined"
					|| "layout" in source
					|| "name" in source.layout
				) {
					throw new Error("Invalid attributes specified.");
				}
				// 頂点データを登録
				attributes[source.layout.name] = this.device.createBuffer(data);
				// layoutを登録
				bufferLayout.push(source.layout);
			});
		} else if (typeof sources === "object") {
			// オブジェクトの場合
			Object.keys(sources).forEach((name) => {
				// 対象を決める
				const source = sources[name];
				// 値の型で分岐
				if (ArrayBuffer.isView(source) === true) {
					// 型付配列の場合はキーでlayoutを作って値でバッファを作りそれぞれ登録
					bufferLayout.push({ name });
					attributes[name] = this.device.createBuffer(source);
				} else if (typeof source === "object") {
					// オブジェクトの場合
					// layout属性があればそれを使い、なければ新規作成して登録
					const layout = source.layout ?? {};
					layout.name = layout.name ?? name;
					bufferLayout.push(layout);
					// 頂点データを決める
					const data = source.data ?? source.values;
					// 頂点データが決まらなければ例外
					if (typeof data === "undefined") {
						throw new Error("Invalid attributes specified.");
					}
					// バッファを作って登録
					attributes[name] = this.device.createBuffer(data);
				} else {
					// 上記以外は例外
					throw new Error("Invalid sources specified.");
				}
			});
		}
		// setBufferLayoutは多分gl.vertexAttribPointerを内部で実行しているから本来はprepareで処理すべき
		// bufferLayoutが設定されていない場合のみ実行（初回1回だけ）
		if (this.model.bufferLayout.length === 0) {
			this.model.setBufferLayout(bufferLayout);
		}
		// 頂点データとポイント数の割当（頂点データの割当は必ずbufferLayout設定後）
		this.model.setAttributes(attributes);
		this.model.setVertexCount(count);
		//----------------------------------------------------------------------
		// 出力先バッファの用意
		this.buffers = {};
		Object.keys(this.varyings).forEach((key) => {
			this.buffers[key] = this.device.createBuffer({
				byteLength: Float32Array.BYTES_PER_ELEMENT * this.varyings[key].length * count
			});
		});
		//----------------------------------------------------------------------
		// transformFeedbackに出力バッファを設定してモデルに登録
		const transformFeedback = this.device.createTransformFeedback({
			layout: this.model.pipeline.shaderLayout,
			buffers: this.buffers
		});
		this.model.setTransformFeedback(transformFeedback);
		//----------------------------------------------------------------------
		// 参照を返す
		return this;
	}

	//**************************************************************************
	/**
	 * バッファからデータを取得して返す
	 * @param {object} buffer
	 * @returns {Float32Array}
	*/
	async readBufferData(buffer) {
		//----------------------------------------------------------------------
		// Uint8Array（バッファ）をFloat32Array（頂点データ）に変換して返す
		const values = await buffer.readAsync();
		return new Float32Array(values.buffer);
	}

	//**************************************************************************
	/**
	 * 実行
	 * @param {Object} [uniforms]
	 * @returns	{Promise<Object>}
	*/
	run(uniforms) {
		//----------------------------------------------------------------------
		// ユニフォーム変数があれば登録
		if (typeof uniforms !== "undefined") {
			this.model.setUniforms(uniforms);
		}
		//----------------------------------------------------------------------
		// 実行
		const renderPass = this.device.beginRenderPass({ discard: true });
		this.model.draw(renderPass);
		renderPass.end();
		//----------------------------------------------------------------------
		// 結果を読み取る
		const result = {};
		const promises = Object.keys(this.buffers).map((key) => {
			return this.readBufferData(this.buffers[key]).then((value) => {
				result[key] = value;
			});
		});
		//----------------------------------------------------------------------
		// 読み取り完了を待ってバッファを破棄する
		return Promise.all(promises).then(() => {
			Object.keys(this.attributes).forEach((key) => {
				this.attributes[key].destroy();
			});
			Object.keys(this.buffers).forEach((key) => {
				this.buffers[key].destroy();
			});
			return result;
		});
	}
};
